/**
 * Holds the player's hands while a scene plays (ADR 0067): a clear layer
 * over the whole page swallows every click and touch, HUD included, and a
 * key listener in the capture phase of the window swallows every key before
 * the game's own listeners hear it. Nothing is sent meanwhile: the server
 * keeps the character where it stood.
 */
export class InputLock {
  private layer: HTMLDivElement | undefined;
  private readonly swallow = (event: KeyboardEvent): void => {
    event.preventDefault();
    event.stopImmediatePropagation();
  };

  get held(): boolean {
    return this.layer !== undefined;
  }

  hold(): void {
    if (this.layer !== undefined) return;
    const layer = document.createElement('div');
    layer.className = 'cinematic-lock';
    layer.setAttribute('aria-hidden', 'true');
    Object.assign(layer.style, {
      position: 'fixed',
      inset: '0',
      zIndex: '10000',
      background: 'transparent',
      cursor: 'default',
      touchAction: 'none',
    });
    for (const type of ['pointerdown', 'pointerup', 'click', 'dblclick', 'contextmenu', 'wheel']) {
      layer.addEventListener(type, (event) => {
        event.preventDefault();
        event.stopPropagation();
      });
    }
    document.body.append(layer);
    // Whatever had the focus (the chat) lets it go: no key reaches it either.
    if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
    window.addEventListener('keydown', this.swallow, true);
    window.addEventListener('keyup', this.swallow, true);
    this.layer = layer;
  }

  release(): void {
    if (this.layer === undefined) return;
    this.layer.remove();
    this.layer = undefined;
    window.removeEventListener('keydown', this.swallow, true);
    window.removeEventListener('keyup', this.swallow, true);
  }
}
